import React, { useEffect } from 'react';
import AnimatedSection from '../components/AnimatedSection';
import { trackPageView } from '../utils/analytics';

const PrivacyPolicy = () => {
  useEffect(() => {
    trackPageView('/privacy-policy', 'Privacy Policy');
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1F44] to-[#0056D2] py-16">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-blue-100">
              Last Updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="prose prose-lg max-w-none space-y-8">
              <div className="bg-[#F5F7FA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">1. Information We Collect</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We collect information that you provide directly to us, including:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Name, email address, phone number, and company details</li>
                  <li>Billing and payment information</li>
                  <li>Tally data and files that you upload to our cloud servers</li>
                  <li>Communications with our support team</li>
                  <li>Usage data and analytics about how you interact with our services</li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 border-2 border-gray-100">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">2. How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Provide, maintain, and improve our cloud hosting services</li>
                  <li>Process your transactions and send related information</li>
                  <li>Send you technical notices, updates, and support messages</li>
                  <li>Respond to your comments, questions, and customer service requests</li>
                  <li>Monitor and analyze trends, usage, and activities</li>
                  <li>Detect, prevent, and address technical issues and security threats</li>
                </ul>
              </div>

              <div className="bg-[#F5F7FA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">3. Data Security</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We take the security of your data seriously. We implement industry-standard security measures including:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>256-bit SSL encryption for data in transit</li>
                  <li>Encrypted storage for data at rest</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Access controls and authentication mechanisms</li>
                  <li>Daily automated backups with version control</li>
                  <li>Disaster recovery and business continuity plans</li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 border-2 border-gray-100">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">4. Data Sharing and Disclosure</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>With your explicit consent</li>
                  <li>To comply with legal obligations or court orders</li>
                  <li>To protect our rights, property, or safety</li>
                  <li>With service providers who assist in our operations (under strict confidentiality agreements)</li>
                </ul>
              </div>

              <div className="bg-[#F5F7FA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">5. Cookies and Tracking</h2>
                <p className="text-gray-700 leading-relaxed">
                  We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some parts of our website may not function properly without cookies.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 border-2 border-gray-100">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">6. Your Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Access and receive a copy of your personal data</li>
                  <li>Rectify inaccurate or incomplete data</li>
                  <li>Request deletion of your data (subject to legal obligations)</li>
                  <li>Object to or restrict processing of your data</li>
                  <li>Data portability to another service provider</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </div>

              <div className="bg-[#F5F7FA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">7. Data Retention</h2>
                <p className="text-gray-700 leading-relaxed">
                  We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. <span className="font-semibold text-[#0056D2]">We will delete your data after 15 days of subscription expiry.</span>
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 border-2 border-gray-100">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">8. Changes to This Policy</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#0056D2] to-[#0A1F44] rounded-2xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-4">9. Contact Us</h2>
                <p className="leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <ul className="space-y-2">
                  <li><strong>Email:</strong> info@tallybharat.com</li>
                  <li><strong>Phone:</strong> +91 9829055959, +91 8094300200</li>
                  <li><strong>Address:</strong> Block H, Boranada Industrial Area, Jodhpur - 342001</li>
                </ul>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;